/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSelectRecord';

const ActionSelectRecordManager: ActionManagerFn<'ActionSelectRecord'> = (
  platform: Platform,
): ActionManager<'ActionSelectRecord'> => {
  return {
    ...baseActionManager(platform, 'ActionSelectRecord'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
      recordIdExpression: {
        label: 'Record ID',
        type: 'expressionOnly',
        returnType: () => 'string',
        required: true,
      },
    },
    getDisplayText: (item) => item.props.alias,
    name: 'Select Record',
    icon: { iconName: 'check-square' },
    description: `**Overview**<br/>
The Select Record action is a precision UI state management component designed to programmatically apply a selection state to a specific record within a Data Store. It serves as the inverse of "Deselect Record," allowing the application to highlight or check a specific item in a grid or list view without requiring direct user interaction.<br/><br/>
This component relies on a specific unique identifier (ID) to locate and select the target record. Because it utilizes an expression for the ID input, the selection logic can be dynamic, enabling the application to react to external events, calculations, or variables to determine which record should be highlighted.

**Key Features**<br/>
* **Targeted Selection:** Operates on a unique record ID, ensuring only the specific intended item is added to the selection set.
* **Dynamic Identification:** Uses an expressionOnly property (recordIdExpression) to define the target ID, allowing for runtime resolution (e.g., selecting a record ID passed via a URL parameter).
* **UI Synchronization:** Immediately updates any UI components (like Data Grids) bound to the target Alias to visually reflect the new selection state.
* **Broad Store Compatibility:** Supports CloudDataStore, DataStore, LocalDataStore, and ReadOnlyDataStore.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property identifying the Data Store containing the record.
* **Record ID (recordIdExpression) (Required):** An expression property.
  * *Type:* expressionOnly returning a string.
  * *Function:* Must evaluate to the unique identifier (ID) of the record to be selected.

**Use Cases**<br/>
* **Deep Linking:** Automatically selecting a specific row in a data grid when the page loads, based on an ID provided in the query string (e.g., opening an invoice details page and highlighting the relevant line item).
* **Creation Feedback:** Automatically selecting a newly created record immediately after it is added to the grid, so the user can see their new entry.
* **External Triggers:** Clicking a pin on a Map component triggers this action to select the corresponding address row in a nearby list component.`,
  };
};

export default ActionSelectRecordManager;
